import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  Clock,
  WifiOff,
  RefreshCw,
  Download,
  TrendingUp,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { API } from '@/config/api';
import ReliabilityDigestSettingsCard from '@/components/backoffice/ReliabilityDigestSettingsCard';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
];

function uptimeTone(pct) {
  if (pct == null) return { fg: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' };
  if (pct > 99) return { fg: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  if (pct >= 95) return { fg: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { fg: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' };
}

function formatDuration(minutes) {
  if (minutes == null || minutes === 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default function KioskReliabilityPage() {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token =
        localStorage.getItem('backoffice_token') ||
        localStorage.getItem('merchant_token') ||
        localStorage.getItem('access_token');
      const res = await axios.get(
        `${API}/owner/kiosks/reliability?range=${range}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Unable to load');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchData]);

  const downloadCsv = useCallback(async () => {
    const token =
      localStorage.getItem('backoffice_token') ||
      localStorage.getItem('merchant_token') ||
      localStorage.getItem('access_token');
    const res = await axios.get(
      `${API}/owner/kiosks/reliability/export.csv?range=${range}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      },
    );
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kiosk_reliability_${range}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [range]);

  const summary = data?.summary;
  const perKiosk = useMemo(() => data?.per_kiosk || [], [data]);
  const timeline = data?.timeline || [];

  const tone = uptimeTone(summary?.uptime_pct);

  const topFailing = useMemo(
    () => [...perKiosk].filter((k) => k.outage_count > 0).slice(0, 5),
    [perKiosk],
  );

  return (
    <div
      data-testid="kiosk-reliability-page"
      className="p-4 sm:p-6 space-y-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-slate-600" />
            Kiosk Reliability
          </h1>
          <p className="text-sm text-slate-500">
            Uptime, MTTR and outage analytics from existing incident history.
            Business-hours aware ({summary?.timezone || 'local TZ'}). Read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="kiosk-reliability-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            data-testid="kiosk-reliability-export-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Range tabs */}
      <div className="flex gap-2 flex-wrap">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            data-testid={`kiosk-reliability-range-${r.key}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              range === r.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error ? (
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Headline summary */}
          <Card
            data-testid="kiosk-reliability-summary"
            className={`border ${tone.border}`}
          >
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4 p-5">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400">
                  Fleet uptime
                </p>
                <p className={`text-3xl font-bold ${tone.fg}`}>
                  {summary?.uptime_pct != null
                    ? `${summary.uptime_pct.toFixed(2)}%`
                    : '—'}
                </p>
                <p className="text-[11px] text-slate-500">
                  {summary?.business_minutes_in_window
                    ? `${formatDuration(summary.business_minutes_in_window)} window`
                    : ''}
                </p>
              </div>
              <SummaryCard
                testId="kiosk-reliability-outage-count"
                icon={<WifiOff className="h-4 w-4 text-rose-500" />}
                label="Outages"
                value={summary?.outage_count ?? 0}
              />
              <SummaryCard
                testId="kiosk-reliability-mttr"
                icon={<Clock className="h-4 w-4 text-slate-500" />}
                label="MTTR"
                value={
                  summary?.mttr_minutes != null
                    ? formatDuration(summary.mttr_minutes)
                    : '—'
                }
              />
              <SummaryCard
                testId="kiosk-reliability-longest"
                icon={<TrendingUp className="h-4 w-4 text-amber-500" />}
                label="Longest outage"
                value={formatDuration(summary?.longest_outage_minutes ?? 0)}
              />
              <SummaryCard
                testId="kiosk-reliability-unresolved"
                icon={<AlertTriangle className="h-4 w-4 text-rose-500" />}
                label="Unresolved"
                value={summary?.unresolved_incident_count ?? 0}
                valueClass={
                  (summary?.unresolved_incident_count ?? 0) > 0
                    ? 'text-rose-600'
                    : 'text-slate-800'
                }
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top failing kiosks */}
            <Card data-testid="kiosk-reliability-top-failing">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top failing kiosks</CardTitle>
              </CardHeader>
              <CardContent>
                {topFailing.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    No outages in this window.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {topFailing.map((k) => (
                      <KioskBar key={k.kiosk_id} kiosk={k} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Uptime leaderboard */}
            <Card data-testid="kiosk-reliability-leaderboard">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Uptime leaderboard ({perKiosk.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-xs text-slate-500 uppercase">
                        <th className="px-3 py-2">Kiosk</th>
                        <th className="px-3 py-2 text-right">Uptime</th>
                        <th className="px-3 py-2 text-right">Down</th>
                        <th className="px-3 py-2 text-right">MTTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perKiosk.slice(0, 50).map((k) => {
                        const t = uptimeTone(k.uptime_pct);
                        return (
                          <tr
                            key={k.kiosk_id}
                            className="border-t border-slate-100 hover:bg-slate-50"
                          >
                            <td className="px-3 py-2 truncate max-w-[180px]">
                              {k.kiosk_name}
                            </td>
                            <td className={`px-3 py-2 text-right font-semibold ${t.fg}`}>
                              {k.uptime_pct.toFixed(2)}%
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600">
                              {formatDuration(k.downtime_minutes)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600">
                              {k.mttr_minutes != null
                                ? formatDuration(k.mttr_minutes)
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly digest settings */}
          <ReliabilityDigestSettingsCard />

          {/* Outage timeline */}
          <Card data-testid="kiosk-reliability-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Outage history ({timeline.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-xs text-slate-500 uppercase">
                      <th className="px-3 py-2">Kiosk</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Started</th>
                      <th className="px-3 py-2">Resolved</th>
                      <th className="px-3 py-2 text-right">Duration</th>
                      <th className="px-3 py-2 text-right">Business min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.slice(0, 100).map((t) => (
                      <tr
                        key={t.incident_id}
                        className="border-t border-slate-100"
                      >
                        <td className="px-3 py-2 truncate max-w-[160px]">
                          {t.kiosk_name || '— (merchant)'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={
                              t.incident_type === 'offline'
                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : 'bg-amber-50 text-amber-800 border-amber-200'
                            }
                          >
                            {t.incident_type}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {new Date(t.started_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {t.ended_at
                            ? new Date(t.ended_at).toLocaleString()
                            : <span className="text-rose-600">ongoing</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">
                          {formatDuration(t.duration_minutes)}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">
                          {t.business_minutes}m
                        </td>
                      </tr>
                    ))}
                    {timeline.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-sm text-slate-500 py-6">
                          No incidents in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, valueClass = 'text-slate-800', testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}

function KioskBar({ kiosk }) {
  const t = uptimeTone(kiosk.uptime_pct);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="truncate max-w-[60%]">{kiosk.kiosk_name}</span>
        <span className={`font-semibold ${t.fg}`}>
          {kiosk.uptime_pct.toFixed(2)}%
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${
            kiosk.uptime_pct > 99
              ? 'bg-emerald-500'
              : kiosk.uptime_pct >= 95
              ? 'bg-amber-500'
              : 'bg-rose-500'
          }`}
          style={{ width: `${Math.max(0, Math.min(100, kiosk.uptime_pct))}%` }}
        />
      </div>
      <div className="text-[11px] text-slate-500">
        {kiosk.outage_count} outage{kiosk.outage_count === 1 ? '' : 's'} ·{' '}
        {formatDuration(kiosk.downtime_minutes)} down
      </div>
    </div>
  );
}
